// src/tenantAdmin/pages/BrandVoicePage.jsx — route /tenant-admin/brand-voice.
//
// The backend's brand-identity editor, shown as "Brand Voice" (Brian Cortex group), for the
// admin of the ACTIVE workspace only (the pathless RequireTenantAdmin group in AppRoutes;
// backend gate TENANT_ADMIN_REQUIRED). No member route; the superadmin mount is deferred, and
// the draft is not kept in sessionStorage (a reload loses it — the leave guard warns).
// Three reads, IN PARALLEL, each failing on its own: GET /brand-identity/document (the five
// document tabs, the voice's lock state), GET /context/brand-voice (the landing tab,
// "Personalità del brand") and its `?language=en` half (whose 404 is "no English voice", not
// an error). ⛔ One failing never blanks another — that is why `useBrandIdentity` keeps
// independent parts with their own `error`, and why the tab strip is built from
// `buildTabs(doc.data?.fields)`, which keeps the five known document tabs even when
// the document read refused: each tab then explains its own state instead of
// vanishing. ⛔ Never gated on credits: editing is free, and this screen reads no
// balance and no entitlements.
// ⛔ Every refusal is worded from `err.code` / `err.status` (brandVoice/copy.js) — never
// `err.message` (the server's English) and never a server `warning`.
//
// ⭐ v184 makes it a COMPOSITION and nothing else: the reads are `useBrandIdentity`, the
// unsaved edits `useBrandDraft`, every write `useBrandSaveActions`, a document tab's body
// `DocFields` and the save area `SaveBar`. What is left here is the chrome — the PageBand,
// the six tabs and the `?tab=` mirror — plus the two screen-level banners and the ONE
// accessor (`rowFor`) that hands each field row its own state. ⛔ It re-derives no rule
// (fieldRules.js) and words no refusal (copy.js).
// ⭐ v190: the landing tab is LIVE — `brandVoice/ui/VoiceTab.jsx` (the voice lock, the
// Italiano / English switch, the per-language notes and `VoiceForm`). The page adds a THIRD
// read (the English voice), owns the language on screen (so `onFocusPath` can switch it) and
// resets it to Italiano on a workspace change.
// ⭐ v199 — base / "Avanzate": ONE page-wide toggle (`useAdvancedView('brand-voice', userKey)`, the same
// pieces as Buyer Personas). v200: DocFields draws it right after the last base field of a tab that has advanced
// fields (Terminologia, Esempi di stile, Messaging) — never on Personalità, Principi or Fatti. What is base and what
// is advanced is `brandVoice/advancedModel.js` alone; the page passes `advanced` and the toggle down to DocFields. A
// new problem list naming a hidden key, or `onFocusPath` on a hidden path, FORCES Avanzate open (`forcedOpen` — an
// override, never persisted; switching the toggle or the workspace clears it). While off, the toggle's badge counts
// hidden problems, unsaved changes and conflicts. v200: every save translates (no "Aggiorna anche l'inglese") and the
// English state shows in every view. The voice name is never shown: the save fills it in ("<workspace> — voce" /
// "— voice", useBrandSaveActions). ⛔ Hiding never changes what a save sends.
// ⛔ Deliberately never called here: `/completeness` (it can start a BILLED refresh), and the
// voice PUT's `strict_mode_activated` is ignored (the runtime still falls back to the global voice).
// Handoff: <BACKEND_FOLDER>/docs/frontend/2026-09-01-brand-identity-editor-handoff.md
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useOnboarding, useRunFinishedEpoch } from '../../onboarding/onboardingStore.js';
import { buildTabs, tabIntroKey, VOICE_TAB } from '../../context/brandVoice/tabs.js';
import { tabHasChanges } from '../../context/brandVoice/draftModel.js';
import useBrandIdentity from '../../context/brandVoice/useBrandIdentity.js';
import useBrandDraft from '../../context/brandVoice/useBrandDraft.js';
import useBrandSaveActions from '../../context/brandVoice/useBrandSaveActions.js';
import useLeaveGuard from '../../context/brandVoice/useLeaveGuard.js';
import { fromWire } from '../../context/brandVoice/wireShape.js';
import { voiceLangOf, voicePath } from '../../context/brandVoice/voiceModel.js';
// ⛔ Every refusal is worded from `err.code` / `err.status` in ONE shared place — never
// `err.message`. The three read helpers live in brandVoice/copy.js since v184.
import { docErrorCopy, docErrorRetryable, voiceErrorCopy } from '../../context/brandVoice/copy.js';
import DocFields from '../../context/brandVoice/ui/DocFields.jsx';
import VoiceTab from '../../context/brandVoice/ui/VoiceTab.jsx';
import SaveBar from '../../context/brandVoice/ui/SaveBar.jsx';
import SegmentedTabs from '../../components/ui/SegmentedTabs.jsx';
import AdvancedToggle from '../../components/ui/AdvancedToggle.jsx';
import useAdvancedView from '../../hooks/useAdvancedView.js';
import { hiddenAttention, isHiddenProblemKey } from '../../context/brandVoice/advancedModel.js';
import { PAGE_COLUMN } from '../../components/ui/SectionCard.jsx';
import PageBand from '../../components/ui/PageBand.jsx';
import PersonalizationBadge from '../../onboarding/ui/PersonalizationBadge.jsx';
import { COLORS, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';

// ── Styles (tokens only) ──────────────────────────────────────────────────────
const CARD = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.section, boxShadow: SHADOW.card, overflow: 'hidden',
};
const banner = (sem) => ({
  marginBottom: 16, padding: '10px 14px', borderRadius: RADII.control,
  background: sem.bg, color: sem.fg, fontSize: 13,
});
// v200: the tab's one-line intro, on top of every tab.
const HEAD_TEXT = { fontSize: 13, color: COLORS.textSecondary, margin: '0 0 10px', lineHeight: '20px' };
const PANEL_MUTED = { fontSize: 13, color: COLORS.textTertiary, margin: 0 };

function ErrorPanel({ t, text, onRetry }) {
  return (
    <div role="alert" style={{ ...banner(SEMANTIC.errore), marginBottom: 0 }}>
      <div>{text}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            marginTop: 10, padding: '6px 12px', borderRadius: RADII.control,
            border: `1px solid ${SEMANTIC.errore.fg}`, background: 'transparent',
            color: SEMANTIC.errore.fg, fontFamily: FONT_SANS, fontSize: 12,
            fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('brandVoice.errors.retry')}
        </button>
      )}
    </div>
  );
}

// ── "Personalità del brand" — the voice tab ───────────────────────────────────
// The ITALIAN read decides whether the tab can be drawn at all (loading / its error panel);
// the English read's own states are shown inside VoiceTab, on the English view only.
// ⚠️ A tenant with no voice of its own is NOT an error: the read answers 200 with
// `source: 'global'` (VoiceTab words it). ⛔ Never derived from `id`.
function VoicePanel({ t, state, onRetry, children }) {
  if (state.loading) return <p style={PANEL_MUTED}>{t('brandVoice.loading')}</p>;
  if (state.error) return <ErrorPanel t={t} text={voiceErrorCopy(t, state.error)} onRetry={onRetry} />;
  return children;
}

// ── The five document tabs (plus any the backend adds later) ──────────────────
// A refused document read replaces the WHOLE body: without `fields[]` there is nothing
// to draw. The rows themselves come from DocFields, handed in as `children`.
function DocPanel({ t, state, onRetry, children }) {
  if (state.loading) return <p style={PANEL_MUTED}>{t('brandVoice.loading')}</p>;
  if (state.error) {
    return (
      <ErrorPanel
        t={t}
        text={docErrorCopy(t, state.error)}
        // Riprova re-reads every half; withheld on 404 / 422, where a retry cannot change the answer.
        onRetry={docErrorRetryable(state.error) ? onRetry : null}
      />
    );
  }
  return children;
}

// ── A screen-level banner with one action ─────────────────────────────────────
function ActionBanner({ sem, text, label, onClick }) {
  return (
    <div role="status" style={banner(sem)}>
      <span>{text}</span>
      {label && (
        <button
          type="button"
          onClick={onClick}
          style={{
            marginLeft: 10, padding: '4px 10px', borderRadius: RADII.controlInner,
            border: `1px solid ${sem.fg}`, background: 'transparent', color: sem.fg,
            fontFamily: FONT_SANS, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {label}
        </button>
      )}
    </div>
  );
}

export default function BrandVoicePage() {
  const { t } = useTranslation('context');
  const { activeTenant, user } = useAuth();
  const tenantId = activeTenant?.tenant_id || null;
  // v199 — base / Avanzate, ONE page-wide preference (the same pieces as Buyer Personas). `forcedOpen` is the
  // override a hidden problem raises (never persisted; switching the toggle clears it).
  const [advanced, setAdvanced] = useAdvancedView('brand-voice', user?.user_id || user?.email || '');
  const [forcedOpen, setForcedOpen] = useState(false);
  const shown = advanced || forcedOpen;
  const toggleAdvanced = (next) => { setForcedOpen(false); setAdvanced(next); };
  useEffect(() => { setForcedOpen(false); }, [tenantId]);
  const [params, setParams] = useSearchParams();

  const {
    doc, voice, voiceEn, reloadDoc, reloadVoice, reloadVoiceEn, reloadAll,
  } = useBrandIdentity(tenantId);
  const fields = doc.data?.fields;
  const draftApi = useBrandDraft({ fields, voiceData: voice.data, voiceEnData: voiceEn.data });
  // ⭐ v190: the voice language on screen — PAGE state (not the URL), because `onFocusPath`
  // must be able to switch it; back to Italiano on a workspace change.
  const [voiceLang, setVoiceLang] = useState('it');
  useEffect(() => { setVoiceLang('it'); }, [tenantId]);
  const voiceDirtyAny = draftApi.voiceIsDirty || draftApi.voiceEnIsDirty;
  // `dirtyCount` counts each voice language as one, so a voice-only draft arms the guard too.
  useLeaveGuard(draftApi.dirtyCount > 0, t('brandVoice.leaveWarning'));

  const tabs = useMemo(
    () => buildTabs(fields).map((tab) => ({
      ...tab,
      label: tab.known ? t(`brandVoice.tabs.${tab.key}`) : tab.key,
      dirty: tab.kind === 'voice' ? voiceDirtyAny : tabHasChanges(tab.key, fields, draftApi.dirty),
    })),
    [fields, draftApi.dirty, voiceDirtyAny, t],
  );
  // ⛔ An unknown `?tab=` falls back to the voice tab — never a blank body.
  const requested = params.get('tab');
  const active = tabs.some((x) => x.key === requested) ? requested : VOICE_TAB;
  const current = tabs.find((x) => x.key === active);
  const currentFields = (fields || []).filter((f) => f.tab === current?.key);
  // The landing tab carries NO param, and `replace` keeps tab switches out of history.
  const selectTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === VOICE_TAB) next.delete('tab'); else next.set('tab', key);
    setParams(next, { replace: true });
  };

  const actions = useBrandSaveActions({
    t, tenantId, doc, voice, voiceEn, draftApi, reloadDoc, reloadVoice, reloadVoiceEn,
    workspaceName: activeTenant?.display_name || '',
    // A problem on a tab nobody is looking at is an invisible problem.
    onFocusPath: (path) => {
      // v199: a problem on a hidden path opens Avanzate first.
      if (isHiddenProblemKey(path, fields)) setForcedOpen(true);
      // ⭐ v190: a voice problem opens the voice tab AND the language it belongs to.
      const lang = voiceLangOf(path);
      if (lang) { selectTab(VOICE_TAB); setVoiceLang(lang); return; }
      const field = (fields || []).find((f) => f.field_path === path);
      if (field?.tab) selectTab(field.tab);
    },
  });
  // v199: every NEW problem list that names a hidden key (server 422s included) forces Avanzate open.
  useEffect(() => {
    if (Object.keys(actions.problems).some((k) => isHiddenProblemKey(k, fields))) setForcedOpen(true);
  }, [actions.problems]); // eslint-disable-line react-hooks/exhaustive-deps -- `fields` is read, not watched
  // While Avanzate is off, what hidden needs attention — the toggle's badge.
  const hidden = shown ? 0 : hiddenAttention({
    fields: fields || [], problems: actions.problems, dirty: draftApi.dirty, conflicts: draftApi.conflicts,
  });
  // An AI update locks the whole screen, the voice included: it rewrites the same file.
  const runLocked = !!doc.data?.run_active || actions.runLockedLocal;
  const busy = actions.busy;

  // v191 — the lock lifts BY ITSELF: when the app-wide poller sees a run end, re-read the document
  // (checkAgain lifts the local lock only if the server agrees) and both voice halves (the run
  // rewrites the voice). Drafts survive every re-read.
  const finishedEpoch = useRunFinishedEpoch();
  const handledEpochRef = useRef(finishedEpoch);
  const liftRef = useRef(null);
  liftRef.current = () => { actions.checkAgain(); reloadVoice(); reloadVoiceEn(); };
  useEffect(() => {
    if (finishedEpoch === handledEpochRef.current) return;
    handledEpochRef.current = finishedEpoch;
    liftRef.current();
  }, [finishedEpoch]);
  // The manual "Controlla di nuovo" stays only where the poller cannot help (no status for this
  // workspace, or a degraded one).
  const onboarding = useOnboarding();
  const autoLift = onboarding.tenantId === tenantId && !!onboarding.status && !onboarding.status.degraded;

  // One accessor per field, so DocFields stays dumb and the page holds no per-row logic.
  const rowFor = (field) => {
    const path = field.field_path;
    const draft = draftApi.draft;
    return {
      ctx: {
        dirty: draftApi.dirty.has(path),
        busy,
        runLocked,
        unlockEffective: doc.data?.unlock_effective,
        enHalfPresent: doc.data?.en_half_present,
        outcome: actions.outcomes[path] || null,
      },
      // ⭐ ALWAYS the editor's shape (wireShape.js): a draft already is one, a stored value is converted.
      value: Object.prototype.hasOwnProperty.call(draft.fields, path) ? draft.fields[path] : fromWire(field, field.value),
      valueEn: Object.prototype.hasOwnProperty.call(draft.fieldsEn, path)
        ? draft.fieldsEn[path] : fromWire(field, field.value_en),
      problems: actions.problems[path] || [],
      conflict: draftApi.conflicts.has(path),
    };
  };

  return (
    // 2026-10-01: the console's white band (PageBand) instead of the "Brian Cortex" group header — the
    // three Brian Cortex pages now share one header; the group name lives in the sidebar only.
    <div>
      {/* 2026-10-01: the six sections sit in the band like the console's tabs — ⛔ still BUTTONS
          (SegmentedTabs variant="band"), never links: the leave guard intercepts in-app link clicks.
          The read-only "Identità" strip was removed (the site is in KB; v201: no workspace name in the subtitle). */}
      <PageBand
        title={t('brandVoice.title')}
        aside={<PersonalizationBadge />}
        // v201: no workspace name in the subtitle.
        subtitle={t('brandVoice.subtitle')}
      >
        <SegmentedTabs
          variant="band"
          tabs={tabs} value={active} onChange={selectTab}
          label={t('brandVoice.tablistLabel')} idPrefix="bv-tab"
          dirtyLabel={t('brandVoice.unsavedTab')}
        />
      </PageBand>
      <div style={PAGE_COLUMN}>
        {/* v191: the lock lifts by itself when the personalization poller sees the run end; the
            manual re-check is only the fallback when that poller has no usable status. */}
        {runLocked && (
          <ActionBanner
            sem={SEMANTIC.info}
            text={t(autoLift ? 'brandVoice.runLockedAuto' : 'brandVoice.runLocked')}
            label={autoLift ? undefined : t('brandVoice.checkAgain')}
            onClick={autoLift ? undefined : actions.checkAgain}
          />
        )}
        {/* The workspace's guidelines are hand-managed: Unlock would move a row the engine
            never reads, so no row offers it and this one line says why. */}
        {doc.data?.unlock_effective === false && (
          <div role="status" style={banner(SEMANTIC.info)}>{t('brandVoice.unlockUnavailable')}</div>
        )}
        <div style={CARD}>
          {/* ⚠️ ONE panel for all six tabs: SegmentedTabs points every tab's
              aria-controls at `${idPrefix}-panel`, so this id must exist exactly once. */}
          <div
            role="tabpanel" id="bv-tab-panel" aria-labelledby={`bv-tab-${active}`}
            style={{ padding: 24, minHeight: 160 }}
          >
            {/* v200: the tab's intro on top; the "Avanzate" toggle is drawn by DocFields right after the last base
                field, where the advanced fields appear. */}
            <p style={HEAD_TEXT}>{tabIntroKey(current) ? t(tabIntroKey(current)) : null}</p>
            {current?.kind === 'voice'
              ? (
                <VoicePanel t={t} state={voice} onRetry={reloadAll}>
                  <VoiceTab
                    it={voice} en={voiceEn} lang={voiceLang} onLang={setVoiceLang}
                    values={{ it: draftApi.voiceValue('it'), en: draftApi.voiceValue('en') }}
                    dirty={{ it: draftApi.voiceIsDirty, en: draftApi.voiceEnIsDirty }}
                    conflicts={draftApi.voiceConflicts}
                    problems={actions.problems}
                    lock={doc.data ? doc.data.voice : null}
                    lockCtx={{ dirty: voiceDirtyAny, busy, runLocked, unlockEffective: doc.data?.unlock_effective }}
                    disabled={busy || runLocked}
                    onChange={(lang, next, key) => {
                      draftApi.setVoice(next, lang);
                      actions.clearProblems(voicePath(lang, key));
                    }}
                    onFreeze={actions.onFreeze}
                    onUnlock={actions.onUnlock}
                    onRetry={reloadAll}
                  />
                </VoicePanel>
              )
              : (
                <DocPanel t={t} state={doc} onRetry={reloadAll}>
                  <DocFields
                    fields={currentFields}
                    rowFor={rowFor}
                    advanced={shown}
                    // One page-wide preference; DocFields draws it only on a tab with advanced fields.
                    toggle={(
                      <AdvancedToggle
                        on={shown} onChange={toggleAdvanced} label={t('brandVoice.advanced.label')}
                        hiddenCount={hidden} hiddenLabel={t('brandVoice.advanced.hidden', { count: hidden })}
                      />
                    )}
                    // Editing a field clears ITS problems at once (the checks run only on save).
                    onChange={(field, next) => {
                      draftApi.setField(field.field_path, next);
                      actions.clearProblems(field.field_path);
                    }}
                    onChangeEn={(field, next) => {
                      draftApi.setFieldEn(field.field_path, next);
                      actions.clearProblems(field.field_path);
                    }}
                    onFreeze={actions.onFreeze}
                    onUnlock={actions.onUnlock}
                    onTranslate={actions.onTranslate}
                  />
                </DocPanel>
              )}
          </div>
          <SaveBar
            count={draftApi.dirtyCount}
            saving={actions.saving} locked={runLocked}
            onCancel={actions.onCancel} onSave={actions.onSave}
            messages={actions.messages}
          />
        </div>
      </div>
    </div>
  );
}
